import { useState } from 'react';
import { Form, Button, Table, Row, Col, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useCreateRentMutation } from '../../slices/rentsApiSlice';

const RentImportScreen = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createRent] = useCreateRentMutation();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const parsed = XLSX.utils.sheet_to_json(sheet);
        setRows(parsed);
        toast.success(`Loaded ${parsed.length} rows from Excel`);
      } catch (error) {
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmitAll = async () => {
    setLoading(true);
    let successCount = 0;

    for (const row of rows) {
      const {
        house,
        tenant,
        landlord,
        startDate,
        endDate,
        lastRentEnd,
        monthlyRent,
        numberOfMonths,
        address,
        date,
        datePaid,
        remark,
      } = row;

      if (!house || !tenant || !landlord || !startDate || !endDate || !monthlyRent || !numberOfMonths || !address || !date) {
        toast.warn(`Skipping incomplete row: ${JSON.stringify(row)}`);
        continue;
      }

      try {
        await createRent({
          house,
          tenant,
          landlord,
          startDate,
          endDate,
          lastRentEnd,
          monthlyRent,
          numberOfMonths,
          address,
          date,
          datePaid,
          remark,
        }).unwrap();
        successCount++;
        toast.success(`Created rent record for tenant: ${tenant}`);
      } catch (err) {
        toast.error(`Error for tenant ${tenant}: ${err?.data?.message || err.error}`);
      }
    }

    setLoading(false);
    toast.info(`Finished import. ${successCount} rent records created.`);
  };

  return (
    <>
      <Row className="mb-4 align-items-end">
        <Col md={6}>
          <h2>Import Rents from Excel</h2>
          <Form.Group controlId="excelFile">
            <Form.Label>Choose Excel File</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={loading}
            />
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Button
            variant="primary"
            onClick={handleSubmitAll}
            disabled={loading || rows.length === 0}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Submit All Rows'
            )}
          </Button>
        </Col>
      </Row>

      {rows.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Tenant</th>
              <th>House</th>
              <th>Landlord</th>
              <th>Start</th>
              <th>End</th>
              <th>Last End</th>
              <th>Months</th>
              <th>Rent</th>
              <th>Address</th>
              <th>Recorded</th>
              <th>Paid</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.tenant}</td>
                <td>{r.house}</td>
                <td>{r.landlord}</td>
                <td>{r.startDate}</td>
                <td>{r.endDate}</td>
                <td>{r.lastRentEnd || '—'}</td>
                <td>{r.numberOfMonths}</td>
                <td>{r.monthlyRent}</td>
                <td>{r.address}</td>
                <td>{r.date}</td>
                <td>{r.datePaid || '—'}</td>
                <td>{r.remark || '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default RentImportScreen;